// Lightweight runtime error logger. Captures last error and, if configured,
// ships it to a remote endpoint using navigator.sendBeacon. Keep this file
// minimal and optional so it can be removed quickly if it causes noise.

import { buildAppsationsFunctionUrl } from '@/lib/appsationsSdkEndpoints';

type SerializedError = {
  message: string;
  stack?: string;
  time: string;
  extra?: Record<string, unknown>;
};

declare global {
  interface Window {
    __lastRuntimeError?: SerializedError | null;
  }
}

function serializeError(err: unknown): SerializedError {
  if (!err) return { message: String(err), time: new Date().toISOString() };
  if (err instanceof Error) return { message: err.message, stack: err.stack ?? undefined, time: new Date().toISOString() };
  try {
    return { message: JSON.stringify(err), time: new Date().toISOString() };
  } catch {
    return { message: String(err), time: new Date().toISOString() };
  }
}

function sendToEndpoint(payload: SerializedError) {
  const enabled = (import.meta.env as { VITE_ENABLE_RUNTIME_LOGGER?: string }).VITE_ENABLE_RUNTIME_LOGGER === '1';
  if (!enabled) return;

  const url =
    (import.meta.env as { VITE_RUNTIME_LOG_URL?: string }).VITE_RUNTIME_LOG_URL ||
    buildAppsationsFunctionUrl('logRuntimeError');
  if (!url) return;

  // If a build-time token is provided, prefer a fetch with a header so the server
  // can validate a token via `X-RUNTIME-LOG-TOKEN` (more secure than query param).
  const token = (import.meta.env as { VITE_RUNTIME_LOG_TOKEN?: string }).VITE_RUNTIME_LOG_TOKEN;

  try {
    if (token && typeof fetch === 'function') {
      // Use keepalive so requests can complete during unload
      void fetch(url, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          'X-RUNTIME-LOG-TOKEN': token,
        },
        body: JSON.stringify(payload),
      }).catch((err) => console.warn('runtimeErrorLogger: failed to send via fetch', err));
      return;
    }

    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    // Fallback: Use sendBeacon so we don't block page unloads or worsen user experience
    navigator.sendBeacon(url, blob);
  } catch (err) {
    // best-effort only
    console.warn('runtimeErrorLogger: failed to send beacon', err);
  }
}

export function initRuntimeErrorLogger() {
  // Store the last runtime error for quick inspection (dev only)
  window.__lastRuntimeError = null;

  window.addEventListener('error', (ev: ErrorEvent) => {
    const ser = serializeError(ev.error ?? ev.message);
    window.__lastRuntimeError = ser;
    sendToEndpoint(ser);
  });

  window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
    const ser = serializeError(ev.reason ?? 'unhandled rejection');
    window.__lastRuntimeError = ser;
    sendToEndpoint(ser);
  });

  // Also capture console.error calls so we can inspect the last error quickly
  const origConsoleError = console.error.bind(console) as (...args: unknown[]) => void;
  console.error = (...args: unknown[]) => {
    try {
      const message = (args as unknown[]).map(a => (a instanceof Error ? a.message : String(a))).join(' ');
      window.__lastRuntimeError = { message, time: new Date().toISOString(), extra: { args: args as unknown[] } };
    } catch (e) {
      // ignore
    }
    origConsoleError(...args);
  };
}

// Initialize on import so main can stay simple. Call is idempotent.
initRuntimeErrorLogger();